import { Component, OnInit } from '@angular/core';

import { saveAs } from "file-saver";

import { AccountService } from '@services/account.service';
import { Subscription } from 'rxjs';
import { DetailPageService } from '@services/support/detail-page.service';
import { AccountDetailsComponent } from '../account-details/account-details.component';
import { Account } from '@models/Account';
import { LoaderService } from '@services/support/loader.service';

@Component({
  selector: 'app-account-list',
  templateUrl: './account-list.component.html',
  styleUrls: ['./account-list.component.scss']
})
export class AccountListComponent implements OnInit {

  licenseTypes = AccountService.licenseTypes
  licenseModels = AccountService.licenseModels
  allFeatures = AccountService.allFeatures
  allAddOns = AccountService.allAddOns
  timezones = AccountService.timezones

  showAllFilters: boolean = false;
  selectedAccountIds: string[] = [];
  expertUsageTimezone: string = 'UTC-3';
  filterValues = {
    demoOrNot: 'all',
    accountType: 'all',
    filterTimezones: [],
    filterLicenseTypes: [],
    filterLicenseModels: [],
    hideAccountsWithNoLicense: true,
    hideDisabled: true,
    filterManagers: [],
    haveFeature: [],
    noFeature: [],
    haveAddOn: [],
    noAddOn: [],
    minSessionCount: null,
    minSessionDuration: null,
    minUserCount: null,
    minRoomCount: null
  };
  filters: {[key: string]: (account: Account) => boolean} = {
    hideDisabled: a => a.disabled !== true,
    hideAccountsWithNoLicense: a => a.no_license !== true
  };
  unFilteredAccounts: Account[] = null;
  accounts: Account[] = null;

  accountsMetaSub: Subscription = null;

  accountManagers = null;
  accountsManagersSub: Subscription = null;

  constructor(
    private accountService: AccountService,
    private detailPageService: DetailPageService,
    private loaderService: LoaderService
  ) { }

  ngOnInit() {
    this.accountsMetaSub = this.accountService.accountsMeta
    .subscribe(accountsMeta => {
      this.unFilteredAccounts = accountsMeta.sort((a, b) => a.company_name.toLocaleLowerCase().localeCompare(b.company_name.toLocaleLowerCase()));
      this.applyAccountFilters();
    });

    this.accountsManagersSub = this.accountService.accountsManagers
    .subscribe(managers => { this.accountManagers = managers; })
  }

  exportFilterResults() {
    const result = [];
    
    for (const account of this.accounts) {
      const a: any = {
        companyName: account.company_name,
        accountManager: this.accountManagers && account.account_manager ? this.accountManagers.find(acm => acm.email === account.account_manager) : "-",
        accountName: account.account_name,
        createDate: new Date(account.create_time).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }),
        remoteAccount: account.license.remote_account,
        workflowAccount: account.license.workflow_account,
        isTrial: account.license.demo,
        isDisabled: account.disabled ? true : false,
        licenseType: account.license.type,
        licenseModel: account.license.model,
        adminEmail: account.admin.email,
        timeZone: account.timezone,
        noOfUser: account.user_count,
        nofActiveUser: account.active_user_count,
        emailRecipientList: account.email_recipients ? account.email_recipients : [],
        durationTotal: `${account.session_duration_hours} hours ${account.session_duration_minutes} minutes`,
        nofSessionsTotal: account.total_session_count/*,
        nofActiveP2PRooms: "NODATA",
        nofActiveNormalRooms: "NODATA",
        lastAppUsageDate: "NODATA",
        lastAdminUsageDate: "NODATA",
        averageDurationTotal: "NODATA",
        durationLastMonth: "NODATA",
        nofSessionsLastMonth: "NODATA",
        averageDurationLastMonth: "NODATA",
        durationLastWeek: "NODATA",
        nofSessionsLastWeek: "NODATA",
        averageDurationLastWeek: "NODATA",
        maxSessionTime: "NODATA",
        minSessionTime: "NODATA"*/
      };
      if (a.remoteAccount) {
        a.remoteStartDate = new Date(account.license.remote_start).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })
        a.remoteEndDate = new Date(account.license.remote_end).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })
        a.remoteAccountDuration = `${Math.floor((account.license.remote_end-account.license.remote_start) / 86400000)} days`
      }
      if (a.workflowAccount) {
        a.workflowStartDate = new Date(account.license.workflow_start).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })
        a.workflowEndDate = new Date(account.license.workflow_end).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })
        a.workflowAccountDuration = `${Math.floor((account.license.workflow_end-account.license.workflow_start) / 86400000)} days`
      }

      if (account.disabled) {
        a.disableTime = account.disable_time;
        /*
        a.nofFailedLoginAttemptsTotal = "NODATA";
        a.nofFailedLoginAttemptsLastMonth = "NODATA";
        a.nofFailedLoginAttemptsLastWeek = "NODATA";
        */
      }

      if (!account.no_license) {
        if (!account.no_remote_license) {
          a.remainingRemoteLicenseDuration = `${Math.floor((account.license.remote_end-Date.now()) / 86400000)} days`;
          if (account.license.demo) {
            a.remoteTrialLeftDays = (account.license.remote_end-Date.now()) / 1000 / 60 / 60 / 24;
          }
        }
        if (!account.no_workflow_license) {
          a.remainingWorkflowLicenseDuration = `${Math.floor((account.license.workflow_end-Date.now()) / 86400000)} days`;
          if (account.license.demo) {
            a.workflowTrialLeftDays = (account.license.workflow_end-Date.now()) / 1000 / 60 / 60 / 24;
          }
        }
      }

      if (account.license.type === "expert_user_based") {
        a.nofSubscriber = account.user_count - account.expert_users.count;
        a.nofExpert = account.expert_users.count;
        a.nofExpertLimit = account.license.limit2;
        a.expertUtilization = account.license.limit2 === 0 ? "0%" : Math.floor((account.expert_users.count / account.license.limit2)*100)+"%";
      }
      result.push(a);
    }
    const jsonStr = JSON.stringify(result, null, 2);
    const blob = new Blob([jsonStr], {type: 'text/plain'});
    saveAs(blob, "export.json");
  }

  demoOrNotFilterChanged() {
    if (this.filterValues.demoOrNot !== 'all') {
      this.filters.demoOrNot = a => this.filterValues.demoOrNot === 'demo' ? a.license.demo : !a.license.demo
    } else if (this.filters.demoOrNot) {
      delete this.filters.demoOrNot;
    }
    this.applyAccountFilters();
  }

  accountTypeFilterChanged() {
    if (this.filterValues.accountType !== 'all') {
      this.filters.accountType = a => this.filterValues.accountType === 'remote' ? a.license.remote_account : a.license.workflow_account
    } else if (this.filters.accountType) {
      delete this.filters.accountType;
    }
    this.applyAccountFilters();
  }

  managerFilterChanged() {
    if (this.filterValues.filterManagers.length > 0) {
      this.filters.managerFilter = a => this.filterValues.filterManagers.includes(a.account_manager)
    } else if (this.filters.managerFilter) {
      delete this.filters.managerFilter;
    }
    this.applyAccountFilters();
  }

  filterTimezonesChanged() {
    if (this.filterValues.filterTimezones.length > 0) {
      this.filters.filterTimezones = a => this.filterValues.filterTimezones.includes(a.timezone)
    } else if (this.filters.filterTimezones) {
      delete this.filters.filterTimezones;
    }
    this.applyAccountFilters();
  }

  filterLicenseTypesChanged() {
    if (this.filterValues.filterLicenseTypes.length > 0) {
      this.filters.filterLicenseTypes = a => this.filterValues.filterLicenseTypes.includes(a.license.type)
    } else if (this.filters.filterLicenseTypes) {
      delete this.filters.filterLicenseTypes;
    }
    this.applyAccountFilters();
  }

  filterLicenseModelsChanged() {
    if (this.filterValues.filterLicenseModels.length > 0) {
      this.filters.filterLicenseModels = a => this.filterValues.filterLicenseModels.includes(a.license.model)
    } else if (this.filters.filterLicenseModels) {
      delete this.filters.filterLicenseModels;
    }
    this.applyAccountFilters();
  }

  haveFeatureFilterChanged() {
    if (this.filterValues.haveFeature.length > 0) {
      this.filters.haveFeature = a => this.filterValues.haveFeature.every(f => a.features[f])
    } else if (this.filters.haveFeature) {
      delete this.filters.haveFeature;
    }
    this.applyAccountFilters();
  }

  noFeatureFilterChanged() {
    if (this.filterValues.noFeature.length > 0) {
      this.filters.noFeature = a => this.filterValues.noFeature.every(f => !a.features[f])
    } else if (this.filters.noFeature) {
      delete this.filters.noFeature;
    }
    this.applyAccountFilters();
  }

  haveAddOnFilterChanged() {
    if (this.filterValues.haveAddOn.length > 0) {
      this.filters.haveAddOn = a => this.filterValues.haveAddOn.every(ao => a.add_ons[ao])
    } else if (this.filters.haveAddOn) {
      delete this.filters.haveAddOn;
    }
    this.applyAccountFilters();
  }

  noAddOnFilterChanged() {
    if (this.filterValues.noAddOn.length > 0) {
      this.filters.noAddOn = a => this.filterValues.noAddOn.every(ao => !a.add_ons[ao])
    } else if (this.filters.noAddOn) {
      delete this.filters.noAddOn;
    }
    this.applyAccountFilters();
  }

  accountPropertyEquals(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) === value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyNotEquals(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) !== value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyGreaterThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) > value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyGreaterEqualThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) >= value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyLessThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) < value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyLessEqualThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) <= value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  applyAccountFilters() {
    let filterIndex = 0;
    let result = true;
    let aid = null;
    const keys = Object.keys(this.filters);
    let filterFn: (a: Account) => boolean = (a) => {
      if (aid !== a.id) {                               // reset temp data if account changed
        aid = a.id;
        filterIndex = 0;
        result = true;
      }
      if (result) {                                     // evaluate previous result
        if (filterIndex < keys.length) {                // if result is true and there is a filter exists to apply
          result = this.filters[keys[filterIndex]](a);  // apply filter
          filterIndex = filterIndex + 1;                // increment filter counter
          return filterFn(a);                           // call self recursively for apply next filter
        } else {                                        // previous filter results true and there is no filter remain
          return true;                                  // return true
        }
      } else {                                          // previous filter results false, so there is no need to apply other filters
        return false;                                   // return false
      }
    }
    this.accounts = this.unFilteredAccounts.filter(filterFn);
  }

  newAccount() {
    const detail = this.detailPageService.loadComponent(AccountDetailsComponent).instance;
    detail.action = AccountDetailsComponent.ACTION_NEW;
  }

  onAccountDetails(e: MouseEvent, account: Account) {
    e.stopPropagation();

    const detail = this.detailPageService.loadComponent(AccountDetailsComponent).instance;
    detail.action = AccountDetailsComponent.ACTION_DETAILS;
    detail.accountId = account.id;
  }

  ngOnDestroy() {
    if (this.accountsMetaSub) { this.accountsMetaSub.unsubscribe() }
    if (this.accountsManagersSub) { this.accountsManagersSub.unsubscribe() }
  }

  trackByFn(index: number, item: Account) {
    return item.id;
  }

  getUsageOfAccounts() {
    this.loaderService.show();
    this.accountService.getUsageStats(this.selectedAccountIds, this.expertUsageTimezone)
      .then((response: any) => {
        const jsonStr = JSON.stringify(response.result, null, 2);
        const blob = new Blob([jsonStr], {type: 'application/json'});
        saveAs(blob, `usage_stats.json`);
      })
      .catch(error => {
        console.error('Error getting usage stats:', error);
      })
      .finally(() => this.loaderService.hide());
  }

  getSessionRatingsOfAccounts() {
    this.loaderService.show();
    this.accountService.getSessionRatingsOfAccounts(this.selectedAccountIds)
      .then((response: any) => {
        const jsonStr = JSON.stringify(response.result, null, 2);
        const blob = new Blob([jsonStr], {type: 'application/json'});
        saveAs(blob, `feedbacks.json`);
      })
      .catch(error => {
        console.error('Error getting feedbacks:', error);
      })
      .finally(() => this.loaderService.hide());

  }
}
